import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, ShoppingBag, Package, EnvelopeSimple, CreditCard, Trash, Check, Minus, Plus } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONDITION_LABELS = {
  new: 'New', like_new: 'Like New', used: 'Used', refurbished: 'Refurbished',
};

export default function ShopItemDetail() {
  const { merchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const { data } = await axios.get(`${API}/merchandise/${merchId}`);
        setItem(data);
      } catch {
        toast.error('Item not found');
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [merchId, navigate]);

  const handleBuy = async () => {
    if (!user) { navigate('/login'); return; }
    setBuying(true);
    try {
      const { data } = await axios.post(`${API}/merchandise/checkout`, {
        merch_id: merchId,
        quantity,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setBuying(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/merchandise/${merchId}`, { withCredentials: true });
      toast.success('Item deleted');
      navigate('/shop');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
      <div className="text-xl font-bold text-[#004E89]">Loading...</div>
    </div>
  );

  if (!item) return null;

  const placeholderImg = 'https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
  const isAdmin = user?.role === 'admin';
  const totalPrice = (item.price * quantity).toFixed(2);

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F6] px-4 py-6" data-testid="shop-item-detail">
      <div className="max-w-5xl mx-auto">
        <Link to="/shop" className="inline-flex items-center gap-2 text-[#5C7A99] hover:text-[#004E89] font-bold text-sm mb-6 transition-colors" data-testid="back-to-shop">
          <ArrowLeft size={18} weight="bold" />
          Back to Shop
        </Link>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Photos */}
          <div>
            <div className="bg-white border-2 border-[#004E89] rounded-2xl overflow-hidden shadow-[4px_4px_0px_#004E89]" data-testid="merch-photo-gallery">
              <div className="h-[350px] sm:h-[420px]">
                <img
                  src={item.photos?.length > 0 ? `${API}/files/${item.photos[activePhoto]}` : placeholderImg}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = placeholderImg; }}
                />
              </div>
              {item.photos?.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto">
                  {item.photos.map((photo, idx) => (
                    <button key={idx} onClick={() => setActivePhoto(idx)}
                      className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all ${idx === activePhoto ? 'border-[#FF6B35] ring-2 ring-[#FF6B35]' : 'border-[#004E89]'}`}
                      data-testid={`merch-photo-thumb-${idx}`}
                    >
                      <img src={`${API}/files/${photo}`} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = placeholderImg; }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Details + Buy */}
          <div className="space-y-5">
            {/* Title & Price */}
            <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
              <div className="flex items-center gap-2 mb-3">
                <span className="tag tag-estate text-[10px]">{CONDITION_LABELS[item.condition] || item.condition}</span>
                {item.category !== 'general' && (
                  <span className="tag bg-[#F9F9F6] text-[#5C7A99] border-[#5C7A99] text-[10px]">{item.category}</span>
                )}
                {!item.in_stock && (
                  <span className="tag bg-red-100 text-red-600 border-red-400 text-[10px]">Sold Out</span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-[#004E89] mb-3" style={{ fontFamily: 'Outfit' }} data-testid="merch-title">
                {item.title}
              </h1>
              <div className="text-3xl font-black text-[#FF6B35] mb-4" style={{ fontFamily: 'Outfit' }} data-testid="merch-price">
                ${item.price.toFixed(2)}
              </div>
              {item.shipping_info && (
                <div className="flex items-center gap-2 text-sm text-[#5C7A99]">
                  <Package size={16} weight="bold" />
                  {item.shipping_info}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
              <h2 className="text-lg font-bold text-[#004E89] mb-3" style={{ fontFamily: 'Outfit' }}>Description</h2>
              <p className="text-[#004E89] leading-relaxed whitespace-pre-wrap text-sm" data-testid="merch-description">{item.description}</p>
            </div>

            {/* Buy Options */}
            {item.in_stock && (
              <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]" data-testid="buy-options">
                {/* Quantity */}
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-sm font-bold text-[#004E89] uppercase tracking-wide">Qty:</span>
                  <div className="flex items-center border-2 border-[#004E89] rounded-xl overflow-hidden">
                    <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="px-3 py-2 hover:bg-[#F9F9F6] transition-colors" data-testid="qty-decrease">
                      <Minus size={16} weight="bold" className="text-[#004E89]" />
                    </button>
                    <span className="px-4 py-2 font-bold text-[#004E89] text-sm border-x-2 border-[#004E89]" data-testid="qty-value">{quantity}</span>
                    <button onClick={() => setQuantity(q => q + 1)} className="px-3 py-2 hover:bg-[#F9F9F6] transition-colors" data-testid="qty-increase">
                      <Plus size={16} weight="bold" className="text-[#004E89]" />
                    </button>
                  </div>
                  <span className="text-sm font-bold text-[#5C7A99]">Total: <span className="text-[#FF6B35]">${totalPrice}</span></span>
                </div>

                {/* Buy Button */}
                <button onClick={handleBuy} disabled={buying}
                  className="btn-primary w-full py-3 flex items-center justify-center gap-2 mb-3"
                  data-testid="buy-now-btn"
                >
                  <CreditCard size={20} weight="bold" />
                  {buying ? 'Processing...' : `Buy Now — $${totalPrice}`}
                </button>

                {/* Contact Option */}
                <a href="mailto:Ltestatesales4all@gmail.com?subject=Inquiry about: ${encodeURIComponent(item.title)}"
                  className="btn-secondary w-full py-3 flex items-center justify-center gap-2 text-center block"
                  data-testid="contact-seller-btn"
                >
                  <EnvelopeSimple size={20} weight="bold" />
                  Contact Seller via Email
                </a>
              </div>
            )}

            {/* Admin Actions */}
            {isAdmin && (
              <button onClick={handleDelete}
                className="btn-outline py-2 px-4 text-sm text-red-600 border-red-400 flex items-center gap-2 shadow-[2px_2px_0px_#dc2626] hover:bg-red-50"
                data-testid="delete-merch-btn"
              >
                <Trash size={16} weight="bold" /> Delete Item
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
